import React from 'react';
import { useTranslation } from 'react-i18next';

import { LineChart, Skeleton, EmptyState } from '@/nova/components';
import type { ChartSeries } from '@/nova/components/LineChart/LineChart';
import { AFFILIATE_METRIC_COLORS, AFFILIATE_REPORT_SERIES } from '@/nova/theme/tokens';
import {
  METRIC_KIND,
  TREND_METRIC_KEYS,
  axisLabels,
  dailyPointLabels,
  dailySeries,
  formatMoneyValue,
  type ChartMode,
  type Dimension,
  type PerfMetricKey,
} from '../../reports-helpers';
import type { AffiliatePerformanceRow } from '../../types';
import type { DimensionSeries } from '../../hooks/useAffiliateReports';
import {
  Panel,
  PanelHead,
  PanelTitle,
  PanelSub,
  Controls,
  Segmented,
  SegBtn,
  Select,
  ChartBody,
  Legend,
  LegendChip,
  Swatch,
} from './dashboard.styles';

export interface TrendChartProps {
  dailyRows: AffiliatePerformanceRow[];
  dimensionSeries: DimensionSeries[];
  dimension: Dimension;
  chartMode: ChartMode;
  metric: PerfMetricKey;
  currency: string;
  loading: boolean;
  onChartMode: (mode: ChartMode) => void;
  onMetric: (metric: PerfMetricKey) => void;
}

export const TrendChart = ({
  dailyRows,
  dimensionSeries,
  dimension,
  chartMode,
  metric,
  currency,
  loading,
  onChartMode,
  onMetric,
}: TrendChartProps): React.ReactElement => {
  const { t } = useTranslation();
  // Literal-keyed so the extractor harvests each label.
  const metricLabel: Record<PerfMetricKey, string> = {
    ngr_usd: t('NGR'),
    ggr_usd: t('GGR'),
    deposits_usd: t('Deposits'),
    registrations: t('Registrations'),
    ftds: t('FTDs'),
    clicks: t('Clicks'),
  };

  // "By dimension" needs a groupable entity axis; the date dimension IS the time axis.
  const byDimensionAvailable = dimension !== 'date';
  const effectiveMode: ChartMode = byDimensionAvailable ? chartMode : 'metric';

  const xLabels = axisLabels(dailyRows);
  const pointLabels = dailyPointLabels(dailyRows);
  const formatY =
    METRIC_KIND[metric] === 'money' ? (n: number): string => formatMoneyValue(n, currency) : undefined;

  const series: ChartSeries[] =
    effectiveMode === 'dimension'
      ? dimensionSeries.map((s, i) => ({
          name: s.name,
          color: AFFILIATE_REPORT_SERIES[i % AFFILIATE_REPORT_SERIES.length],
          points: dailySeries(s.rows, metric),
        }))
      : [{ name: metricLabel[metric], color: AFFILIATE_METRIC_COLORS[metric], points: dailySeries(dailyRows, metric) }];

  const hasData = series.some((s) => s.points.length > 1);

  return (
    <Panel>
      <PanelHead>
        <div>
          <PanelTitle text={t('Performance over time')} tag="h3" />
          <PanelSub
            text={effectiveMode === 'dimension' ? t('One line per top affiliate') : t('Daily trend')}
            tag="span"
          />
        </div>
        <Controls>
          <Segmented role="group" aria-label={t('Chart mode')}>
            <SegBtn type="button" $active={effectiveMode === 'metric'} onClick={() => onChartMode('metric')}>
              {t('Metric')}
            </SegBtn>
            <SegBtn
              type="button"
              $active={effectiveMode === 'dimension'}
              disabled={!byDimensionAvailable}
              onClick={() => byDimensionAvailable && onChartMode('dimension')}
            >
              {t('By dimension')}
            </SegBtn>
          </Segmented>
          <Select value={metric} aria-label={t('Chart metric')} onChange={(e) => onMetric(e.target.value as PerfMetricKey)}>
            {TREND_METRIC_KEYS.map((key) => (
              <option key={key} value={key}>
                {metricLabel[key]}
              </option>
            ))}
          </Select>
        </Controls>
      </PanelHead>

      <ChartBody>
        {loading && !hasData ? (
          <Skeleton height={240} block />
        ) : hasData ? (
          <LineChart series={series} xLabels={xLabels} pointLabels={pointLabels} height={240} showY formatY={formatY} area={series.length === 1} />
        ) : (
          <EmptyState text={t('No data for this period yet')} />
        )}
      </ChartBody>

      {effectiveMode === 'dimension' && series.length > 0 ? (
        <Legend>
          {series.map((s) => (
            <LegendChip key={s.name}>
              <Swatch $color={s.color} />
              {s.name}
            </LegendChip>
          ))}
        </Legend>
      ) : null}
    </Panel>
  );
};

export default TrendChart;
